import PermissionButton from 'components/buttons/PermissionButton';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth.ts';
import cloneDeep from 'lodash/cloneDeep';
import { FormattedMessage } from 'react-intl';
import { CalculatedPricelist, ScopeType } from 'types/pricelists';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { LabelButtons } from './buttons/LabelButtons';
import { PricelistDetailsFetchingCard } from './PricelistDetailsFetchingCard';
import { getMattersLinks } from './util';

export interface GroupedPricelists {
    clientName: string;
    clientId: number | undefined;
    clientFirstName?: string;
    clientLastName?: string;
    pricelists: CalculatedPricelist[];
}

type Props = {
    pricelistGroup: GroupedPricelists;
    openEditModal: (editableEntity: CalculatedPricelist) => void;
};

type PricelistRow = {
    name: JSX.Element;
    priceRange: JSX.Element;
    connectedCases: JSX.Element;
    labels: JSX.Element;
    editButton: JSX.Element | null;
};

// A case row has no price-list id of its own (the API leaves it empty on purpose), so keying every row by
// `id` gave all case rows one key and left stale rows behind on a filter change. The case list the row
// stands for tells it apart; a case that has no list yet falls back to the case itself.
const rowKey = (pricelist: CalculatedPricelist): string => {
    if (pricelist.id != null) return `list-${pricelist.id}`;
    if (pricelist.matterPricelistId != null) return `case-list-${pricelist.matterPricelistId}`;
    return `case-${pricelist.matterId}`;
};

export const PricelistGroupTable = ({ pricelistGroup, openEditModal }: Props) => {
    const { user } = useAuth();
    const permissions = user?.permissions;
    const isAllowedToUpdate = !!permissions?.find((permission) => permission.code === 'UPDATE.PRICELISTS.MODEL');

    const createData = (pricelist: CalculatedPricelist): PricelistRow => {
        const { contractId, contractName, scopeTypeCode } = pricelist;

        const name = (
            <div>
                <div className="font-medium">{pricelist.name}</div>
                {contractId ? (
                    <div>
                        <RecordLink
                            className="entity-link text-fg hover:text-link no-underline hover:underline"
                            to={`/contract/${contractId}`}
                            label={contractName ?? ''}
                        />
                    </div>
                ) : null}
            </div>
        );
        const priceRange = <PricelistDetailsFetchingCard simpleView pricelist={pricelist} />;
        const connectedCases = (
            <div className="flex flex-col justify-start whitespace-normal">
                {getMattersLinks('matter', cloneDeep(pricelist.matters || []))}
            </div>
        );
        const labels = (
            <LabelButtons
                labels={pricelist.labelsList || []}
                pricingKind={pricelist.pricingKind}
                containsExceptions={pricelist.containsExceptions}
            />
        );
        const isEditable = scopeTypeCode === ScopeType.COMPANY || scopeTypeCode === ScopeType.TENANT;
        const editButtonRow = (
            // The row is the tooltip's hover target: a disabled button fires no pointer events of its own.
            <div className="flex flex-nowrap items-center justify-end">
                <PermissionButton
                    permissions={['UPDATE.PRICELISTS.MODEL']}
                    variant="outlined"
                    size="extraSmall"
                    onClick={() => {
                        openEditModal(pricelist);
                    }}
                    disabled={!isEditable}
                >
                    <FormattedMessage id="edit" />
                </PermissionButton>
            </div>
        );
        let editButton: JSX.Element | null = null;
        if (isAllowedToUpdate) {
            editButton = isEditable ? (
                editButtonRow
            ) : (
                <Tooltip
                    content={
                        scopeTypeCode === ScopeType.MATTER
                            ? t('pricelists.edit-under-case')
                            : t('pricelists.edit-under-contract')
                    }
                >
                    {editButtonRow}
                </Tooltip>
            );
        }

        return { name, labels, priceRange, connectedCases, editButton };
    };

    const pricelists = cloneDeep(pricelistGroup.pricelists);

    return (
        <div className="border-divider bg-paper overflow-hidden rounded-[4px] border not-first-of-type:mt-6">
            <div className="border-divider bg-grey-50 flex items-center border-b px-4 py-[9px]">
                <div className="text-h5">
                    {pricelistGroup.clientId ? (
                        <PartyName
                            party={{
                                id: pricelistGroup.clientId,
                                name: pricelistGroup.clientName,
                                firstName: pricelistGroup.clientFirstName,
                                lastName: pricelistGroup.clientLastName,
                            }}
                            isLink
                            variant="list"
                        />
                    ) : (
                        pricelistGroup.clientName
                    )}
                </div>
            </div>
            <table className="w-full text-left text-sm">
                <TableHeader className="[&_tr]:border-b-0">
                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-[30%]')}>{t('pricelists.name')}</TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-[20%]')}>&nbsp;</TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-[20%]')}>
                            {t('pricelists.hourly-rates')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-[25%]')}>
                            {t('pricelists.connected-cases')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-[5%]')}>&nbsp;</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {pricelists
                        .sort((a, b) =>
                            JSON.stringify(b.labelsList ?? '').localeCompare(JSON.stringify(a.labelsList ?? ''))
                        )
                        .map((pricelist) => {
                            const row: PricelistRow = createData(pricelist);
                            return (
                                <TableRow key={rowKey(pricelist)} className={SIMPLE_TABLE_ROW_CLASS}>
                                    <TableCell className={cn(SIMPLE_TABLE_CELL_CLASS, 'w-[30%]')}>{row.name}</TableCell>
                                    <TableCell className={cn(SIMPLE_TABLE_CELL_CLASS, 'w-[20%]')}>
                                        {row.labels}
                                    </TableCell>
                                    <TableCell className={cn(SIMPLE_TABLE_CELL_CLASS, 'w-[20%]')}>
                                        {row.priceRange}
                                    </TableCell>
                                    <TableCell className={cn(SIMPLE_TABLE_CELL_CLASS, 'w-[25%]')}>
                                        {row.connectedCases}
                                    </TableCell>
                                    <TableCell className={cn(SIMPLE_TABLE_CELL_CLASS, 'w-[5%]')}>
                                        {row.editButton}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                </TableBody>
            </table>
        </div>
    );
};
